import React from "react";

/**
 * A screen that throws takes the screen down, not the app.
 *
 * There was no boundary anywhere, so one bad value rendered a white page —
 * and once that value was in storage, reloading re-ran the crash rather than
 * clearing it. The only escape a person could find was clearing site data,
 * which takes the collection, the lists and the goals along with whatever the
 * actual problem was.
 *
 * So the offer here is narrow on purpose: forget the one key this screen
 * keeps, and nothing else. That is almost always the thing that is wrong, and
 * it is never somebody's collection.
 *
 * The specific fault that prompted this is fixed at the source — the stack
 * validates every card it reads now. This is for the next one.
 */
export default class ScreenBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // Not sent anywhere. It is here so a person who opens the console has
    // something to paste at somebody, which is the whole of our error
    // reporting and is honest about it.
    // eslint-disable-next-line no-console
    console.error("Screen failed:", error);
  }

  /* The non-destructive half. A render fault that is nothing to do with
     storage is fixed by this and by nothing else on the screen. */
  reloadOnly = () => {
    window.location.reload();
  };

  reset = () => {
    const { storageKey } = this.props;
    if (storageKey) {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        /* if storage cannot be written to, it is not what is broken */
      }
    }
    // A full reload, because the component that threw cannot be trusted to
    // rebuild itself cleanly from a state that made it throw.
    window.location.reload();
  };

  render() {
    if (!this.state.failed) return this.props.children;

    const { title = "This screen", what = "what it was showing" } = this.props;
    return (
      <div className="screen-failed" role="alert">
        <h2>{title} stopped working</h2>
        {/* It said "something it had saved could not be read", which this
            cannot know: a plain bug in the code raises exactly the same thing.
            Asserting a cause here is not harmless — it points at the saved
            data, and the offer underneath then destroys it to fix something
            that was never wrong with it. */}
        <p>
          It couldn&rsquo;t be displayed. Nothing else has been touched — your
          collection, lists and goals are all still here.
        </p>
        {/* Reloading first, because most faults are not the saved data and a
            reload costs nothing. The destructive option stays, second and
            named, for when reloading has already failed. */}
        <div className="screen-failed-actions">
          <button type="button" className="btn btn-primary" onClick={this.reloadOnly}>
            Reload the page
          </button>
          <button type="button" className="btn btn-secondary" onClick={this.reset}>
            Still broken? Forget {what}
          </button>
        </div>
      </div>
    );
  }
}
